import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Calendar,
  Plus,
  Check,
  X,
  Loader2,
  Settings2,
  Save,
  BarChart3,
  Trash2,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusCfg = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  approved: { label: 'Approved', icon: CheckCircle2, color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const LeavesPage = () => {
  const { user, isHR } = useAuth();
  const [activeTab, setActiveTab] = useState('balance');
  const [leaves, setLeaves] = useState([]);
  const [balance, setBalance] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const [isPolicyOpen, setIsPolicyOpen] = useState(false);
  const [historyFilter, setHistoryFilter] = useState(() => new Date().toISOString().slice(0, 7));
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [newLeave, setNewLeave] = useState({ leave_type: 'PL', start_date: '', end_date: '', reason: '', day_type: 'full', half_session: 'first_half' });
  const [policyForm, setPolicyForm] = useState(null);
  const [holidays, setHolidays] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [leavesRes, balanceRes, policyRes, holidaysRes] = await Promise.all([
        axios.get(`${API_URL}/api/leaves`, { withCredentials: true }),
        axios.get(`${API_URL}/api/leave-balance/${user?.id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/leave-policy`, { withCredentials: true }),
        axios.get(`${API_URL}/api/holidays`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setLeaves(leavesRes.data);
      setBalance(balanceRes.data);
      setPolicy(policyRes.data);
      setHolidays(Array.isArray(holidaysRes.data) ? holidaysRes.data : (holidaysRes.data?.holidays || []));
    } catch (err) {
      console.error('Failed to fetch leave data:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Warn the moment a holiday/weekend date is picked for a leave
  const checkHolidayDate = (dateStr) => {
    if (!dateStr) return;
    const h = holidays.find(x => x.date === dateStr);
    if (h) {
      toast.warning(`${dateStr} is a holiday (${h.name || 'Holiday'}) — you don't need to apply leave for it.`);
      return;
    }
    const wd = new Date(`${dateStr}T00:00:00`).getDay();
    if (wd === 0 || wd === 6) {
      toast.warning(`${dateStr} falls on a weekend.`);
    }
  };

  useEffect(() => { if (user?.id) fetchData(); }, [fetchData, user?.id]);

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    const payload = { ...newLeave };
    if (payload.day_type === 'half') {
      payload.end_date = payload.start_date; // half-day is a single calendar day
    } else {
      payload.half_session = null;
    }
    try {
      await axios.post(`${API_URL}/api/leaves`, payload, { withCredentials: true });
      toast.success('Leave applied successfully');
      setIsApplyOpen(false);
      if (payload.start_date) setHistoryFilter(payload.start_date.slice(0, 7));
      setNewLeave({ leave_type: 'PL', start_date: '', end_date: '', reason: '', day_type: 'full', half_session: 'first_half' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply leave');
    }
  };

  const HALF_DAY_TYPES = ['SL', 'PL'];
  const isHalfEligible = HALF_DAY_TYPES.includes(newLeave.leave_type);

  const handleLeaveAction = async (leaveId, status) => {
    try {
      await axios.put(`${API_URL}/api/leaves/${leaveId}/approve`, { status, remarks: '' }, { withCredentials: true });
      toast.success(`Leave ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${status} leave`);
    }
  };

  const handleCancelApprovedLeave = async (leaveId) => {
    const reason = window.prompt('Enter cancellation reason (e.g. wrong dates, employee at office):');
    if (!reason || reason.trim().length < 3) {
      if (reason !== null) toast.error('Reason must be at least 3 characters');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/leaves/${leaveId}/cancel`, { reason: reason.trim() }, { withCredentials: true });
      toast.success('Leave cancelled — quota refunded');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cancellation failed');
    }
  };

  const openPolicyEditor = () => {
    setPolicyForm(policy ? JSON.parse(JSON.stringify(policy)) : {
      financial_year_start: '01-01',
      financial_year_end: '12-31',
      leave_types: [
        { code: 'PL', name: 'Paid Leave', annual_quota: 12, carry_forward: false, max_carry: 0 },
      ],
      optional_holidays: [],
    });
    setIsPolicyOpen(true);
  };

  const addLeaveType = () => {
    setPolicyForm(prev => ({
      ...prev,
      leave_types: [...(prev.leave_types || []), { code: '', name: '', annual_quota: 0, carry_forward: false, max_carry: 0, admin_controlled: false }]
    }));
  };

  const removeLeaveType = (idx) => {
    setPolicyForm(prev => ({
      ...prev,
      leave_types: prev.leave_types.filter((_, i) => i !== idx)
    }));
  };

  const updateLeaveType = (idx, field, value) => {
    setPolicyForm(prev => {
      const types = [...prev.leave_types];
      types[idx] = { ...types[idx], [field]: value };
      return { ...prev, leave_types: types };
    });
  };

  const addOptionalHoliday = () => {
    setPolicyForm(prev => ({
      ...prev,
      optional_holidays: [...(prev.optional_holidays || []), { name: '', date: '' }]
    }));
  };

  const removeOptionalHoliday = (idx) => {
    setPolicyForm(prev => ({
      ...prev,
      optional_holidays: prev.optional_holidays.filter((_, i) => i !== idx)
    }));
  };

  const updateOptionalHoliday = (idx, field, value) => {
    setPolicyForm(prev => {
      const ohs = [...prev.optional_holidays];
      ohs[idx] = { ...ohs[idx], [field]: value };
      return { ...prev, optional_holidays: ohs };
    });
  };

  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      await axios.put(`${API_URL}/api/leave-policy`, policyForm, { withCredentials: true });
      toast.success('Leave policy saved');
      setIsPolicyOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save policy');
    } finally {
      setSavingPolicy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const leaveTypeOptions = policy?.leave_types?.filter(lt => !lt.admin_controlled)?.map(lt => ({ code: lt.code, name: lt.name })) || [
    { code: 'PL', name: 'Paid Leave' },
    { code: 'SL', name: 'Sick Leave' },
    { code: 'CL', name: 'Casual Leave' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leaves-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="w-7 h-7 text-primary" />
            Leave Management
          </h1>
          <p className="text-muted-foreground">Apply for leaves and track your balance</p>
        </div>
        <div className="flex gap-2">
          {isHR && (
            <Button variant="outline" onClick={openPolicyEditor} className="gap-2" data-testid="configure-policy-btn">
              <Settings2 className="w-4 h-4" /> Leave Policy
            </Button>
          )}
          <Button onClick={() => setIsApplyOpen(true)} className="gap-2" data-testid="apply-leave-btn">
            <Plus className="w-4 h-4" /> Apply Leave
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="leave-tabs">
          <TabsTrigger value="balance" data-testid="tab-balance">
            <BarChart3 className="w-4 h-4 mr-1.5" /> My Balance
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <Calendar className="w-4 h-4 mr-1.5" /> Leave History
            {leaves.filter(l => l.status === 'pending').length > 0 && (
              <span className="ml-1.5 w-5 h-5 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">
                {leaves.filter(l => l.status === 'pending').length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Balance Tab */}
        <TabsContent value="balance" className="mt-6">
          {balance ? (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card data-testid="total-allocated">
                  <CardContent className="pt-5 pb-4 text-center">
                    <p className="text-3xl font-bold text-primary">{balance.total_allocated}</p>
                    <p className="text-xs text-muted-foreground mt-1">Allocated (Pro-rata)</p>
                  </CardContent>
                </Card>
                <Card data-testid="total-taken">
                  <CardContent className="pt-5 pb-4 text-center">
                    <p className="text-3xl font-bold text-orange-500">{balance.total_taken}</p>
                    <p className="text-xs text-muted-foreground mt-1">Taken</p>
                  </CardContent>
                </Card>
                <Card data-testid="total-available">
                  <CardContent className="pt-5 pb-4 text-center">
                    <p className="text-3xl font-bold text-green-500">{balance.total_available}</p>
                    <p className="text-xs text-muted-foreground mt-1">Available</p>
                  </CardContent>
                </Card>
                <Card data-testid="lop-days">
                  <CardContent className="pt-5 pb-4 text-center">
                    <p className="text-3xl font-bold text-red-500">{balance.loss_of_pay}</p>
                    <p className="text-xs text-muted-foreground mt-1">Loss of Pay</p>
                  </CardContent>
                </Card>
              </div>

              <p className="text-xs text-muted-foreground">
                Leave Year: {balance.leave_year || balance.financial_year} | Joining: {balance.joining_date} | Months in Year: {balance.months_in_year || balance.months_in_fy}
              </p>

              {/* Leave Type Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {balance.balances.map((b) => (
                  <Card key={b.code} data-testid={`balance-${b.code}`} className={b.admin_controlled ? 'border-violet-200 dark:border-violet-800' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-semibold text-sm">{b.name}</p>
                          <div className="flex items-center gap-1.5">
                            <Badge variant="outline" className="text-xs">{b.code}</Badge>
                            {b.admin_controlled && <Badge className="text-[10px] bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 border-0">Admin</Badge>}
                          </div>
                        </div>
                        <p className="text-2xl font-bold text-primary">{b.admin_controlled ? b.taken : b.available}</p>
                      </div>
                      {b.admin_controlled ? (
                        <p className="text-xs text-muted-foreground">Granted by admin upon approval. {b.taken} day(s) used.</p>
                      ) : (
                        <>
                          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                            <div
                              className="bg-primary h-full rounded-full transition-all"
                              style={{ width: `${b.pro_rata_allocation > 0 ? Math.min(100, (b.taken / b.pro_rata_allocation) * 100) : 0}%` }}
                            />
                          </div>
                          <div className="flex justify-between text-xs text-muted-foreground mt-1.5">
                            <span>Taken: {b.taken}</span>
                            <span>Allocated: {b.pro_rata_allocation}</span>
                            <span>Annual: {b.annual_quota}</span>
                          </div>
                          {b.half_day_eligible && (
                            <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground border-t pt-1.5" data-testid={`half-day-info-${b.code}`}>
                              <Clock className="w-3 h-3" />
                              Half-days: {b.half_day_used} / {b.half_day_allowance} day(s) used
                            </div>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Optional Holidays */}
              {balance.optional_holidays?.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Optional Holidays (Pick any {policy?.leave_types?.find(l => l.code === 'OH')?.annual_quota || 2})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {balance.optional_holidays.map((oh, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 rounded-lg border text-sm">
                          <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                          <div>
                            <p className="font-medium">{oh.name}</p>
                            <p className="text-xs text-muted-foreground">{oh.date}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <AlertTriangle className="w-12 h-12 text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground">Leave policy not configured</p>
                {isHR && <p className="text-sm text-muted-foreground/70 mt-1">Click "Leave Policy" to set up the policy</p>}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          {(() => {
            const now = new Date();
            const currentYM = now.toISOString().slice(0, 7); // YYYY-MM
            // Sort desc by start_date, then apply month filter
            const sorted = [...leaves].sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''));
            const filtered = historyFilter === 'all' ? sorted : sorted.filter(l => (l.start_date || '').startsWith(historyFilter));
            // Distinct months for the dropdown (newest first) + always include current month
            const monthSet = new Set([currentYM, ...leaves.map(l => (l.start_date || '').slice(0, 7)).filter(Boolean)]);
            const monthList = Array.from(monthSet).sort().reverse();
            return (
              <>
                <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="history-filter-bar">
                  <span className="text-xs text-muted-foreground">Filter:</span>
                  <select
                    value={historyFilter}
                    onChange={e => setHistoryFilter(e.target.value)}
                    className="h-8 rounded-md border bg-background px-2 text-sm"
                    data-testid="history-month-select"
                  >
                    <option value="all">All time</option>
                    {monthList.map(m => (
                      <option key={m} value={m}>
                        {new Date(m + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}{m === currentYM ? ' (current)' : ''}
                      </option>
                    ))}
                  </select>
                  <span className="text-xs text-muted-foreground ml-auto">{filtered.length} record(s) · newest first</span>
                </div>
                {filtered.length === 0 ? (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                      <Calendar className="w-12 h-12 text-muted-foreground/30 mb-3" />
                      <p className="text-muted-foreground">No leave records for the selected period</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {filtered.map((leave) => {
                      const scfg = statusCfg[leave.status] || statusCfg.pending;
                      const Icon = scfg.icon;
                      const typeName = policy?.leave_types?.find(lt => lt.code === leave.leave_type)?.name || leave.leave_type;
                      return (
                        <Card key={leave.id} data-testid={`leave-${leave.id}`}>
                          <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">
                              {leave.employee_name || 'Employee'} — {typeName}
                            </p>
                            <Badge variant="outline" className="text-xs">{leave.leave_type}</Badge>
                            {leave.day_type === 'half' && (
                              <Badge className="text-[10px] bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400 border-0" data-testid={`half-day-badge-${leave.id}`}>
                                Half Day{leave.half_session === 'second_half' ? ' · PM' : ' · AM'}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {leave.start_date}{leave.day_type === 'half' ? '' : ` to ${leave.end_date}`} | {leave.reason}
                          </p>
                          {leave.day_type !== 'half' && leave.total_days != null && (
                            <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`leave-days-${leave.id}`}>
                              {leave.total_days} working day{leave.total_days === 1 ? '' : 's'} deducted (holidays & weekends excluded)
                            </p>
                          )}
                          {leave.remarks && (
                            <p className="text-xs text-muted-foreground mt-0.5">Remarks: {leave.remarks}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${scfg.color}`}>
                            <Icon className="w-3 h-3" />{scfg.label}
                          </span>
                          {isHR && leave.status === 'pending' && (
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-green-600" onClick={() => handleLeaveAction(leave.id, 'approved')} data-testid={`approve-leave-${leave.id}`}>
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => handleLeaveAction(leave.id, 'rejected')} data-testid={`reject-leave-${leave.id}`}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                          {isHR && leave.status === 'approved' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 gap-1 text-xs text-amber-700 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                              onClick={() => handleCancelApprovedLeave(leave.id)}
                              data-testid={`cancel-leave-${leave.id}`}
                              title="Cancel this approved leave (only if payslip not yet generated for the leave month)"
                            >
                              <X className="w-3 h-3" /> Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
                  </div>
                )}
              </>
            );
          })()}
        </TabsContent>
      </Tabs>

      {/* Apply Leave Dialog */}
      <Dialog open={isApplyOpen} onOpenChange={setIsApplyOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Apply Leave</DialogTitle>
            <DialogDescription>Submit a leave request for approval</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleApplyLeave} className="space-y-4">
            <div className="space-y-2">
              <Label>Leave Type</Label>
              <Select value={newLeave.leave_type} onValueChange={(v) => setNewLeave({ ...newLeave, leave_type: v, day_type: HALF_DAY_TYPES.includes(v) ? newLeave.day_type : 'full' })}>
                <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {leaveTypeOptions.map(lt => (
                    <SelectItem key={lt.code} value={lt.code}>{lt.name} ({lt.code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Duration: Full Day / Half Day (half only for SL & PL) */}
            <div className="space-y-2">
              <Label>Duration</Label>
              <div className="flex gap-2" data-testid="leave-daytype-toggle">
                <button
                  type="button"
                  onClick={() => setNewLeave({ ...newLeave, day_type: 'full' })}
                  className={`flex-1 h-9 rounded-md border text-sm font-medium transition-colors ${newLeave.day_type === 'full' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                  data-testid="daytype-full-btn"
                >
                  Full Day
                </button>
                <button
                  type="button"
                  disabled={!isHalfEligible}
                  onClick={() => isHalfEligible && setNewLeave({ ...newLeave, day_type: 'half', end_date: newLeave.start_date })}
                  className={`flex-1 h-9 rounded-md border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${newLeave.day_type === 'half' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                  data-testid="daytype-half-btn"
                >
                  Half Day (0.5)
                </button>
              </div>
              {!isHalfEligible && (
                <p className="text-[11px] text-muted-foreground">Half-day is available only for Sick Leave (SL) and Paid Leave (PL).</p>
              )}
              {isHalfEligible && newLeave.day_type === 'half' && (() => {
                const b = balance?.balances?.find(x => x.code === newLeave.leave_type);
                return b?.half_day_eligible ? (
                  <p className="text-[11px] text-muted-foreground" data-testid="half-day-quota-hint">
                    Half-days used this year: {b.half_day_used} / {b.half_day_allowance} day(s) for {newLeave.leave_type}.
                  </p>
                ) : null;
              })()}
            </div>

            {newLeave.day_type === 'half' && (
              <div className="space-y-2">
                <Label>Session</Label>
                <Select value={newLeave.half_session} onValueChange={(v) => setNewLeave({ ...newLeave, half_session: v })}>
                  <SelectTrigger data-testid="half-session-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first_half">First Half (Morning)</SelectItem>
                    <SelectItem value="second_half">Second Half (Afternoon)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {newLeave.day_type === 'half' ? (
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={newLeave.start_date} onChange={(e) => { setNewLeave({ ...newLeave, start_date: e.target.value, end_date: e.target.value }); checkHolidayDate(e.target.value); }} required data-testid="leave-start-date" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input type="date" value={newLeave.start_date} onChange={(e) => { setNewLeave({ ...newLeave, start_date: e.target.value }); checkHolidayDate(e.target.value); }} required data-testid="leave-start-date" />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input type="date" value={newLeave.end_date} onChange={(e) => { setNewLeave({ ...newLeave, end_date: e.target.value }); checkHolidayDate(e.target.value); }} required data-testid="leave-end-date" />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea value={newLeave.reason} onChange={(e) => setNewLeave({ ...newLeave, reason: e.target.value })} placeholder="Reason for leave" required rows={2} data-testid="leave-reason" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsApplyOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-leave-btn">Submit</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Leave Policy Dialog (HR/Admin) */}
      <Dialog open={isPolicyOpen} onOpenChange={setIsPolicyOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings2 className="w-5 h-5" /> Leave Policy Configuration</DialogTitle>
            <DialogDescription>Define leave types, quotas, and optional holidays for the financial year (April - March)</DialogDescription>
          </DialogHeader>

          {policyForm && (
            <div className="space-y-6">
              {/* Leave Types */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Leave Types</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addLeaveType} className="gap-1" data-testid="add-leave-type-btn">
                    <Plus className="w-3.5 h-3.5" /> Add Type
                  </Button>
                </div>
                <div className="space-y-3">
                  {policyForm.leave_types?.map((lt, idx) => (
                    <div key={idx} className="flex items-end gap-2 p-3 rounded-lg border bg-muted/30">
                      <div className="space-y-1 w-20">
                        <Label className="text-xs">Code</Label>
                        <Input value={lt.code} onChange={(e) => updateLeaveType(idx, 'code', e.target.value.toUpperCase())} placeholder="PL" className="h-9 text-sm" data-testid={`lt-code-${idx}`} />
                      </div>
                      <div className="space-y-1 flex-1">
                        <Label className="text-xs">Name</Label>
                        <Input value={lt.name} onChange={(e) => updateLeaveType(idx, 'name', e.target.value)} placeholder="Paid Leave" className="h-9 text-sm" data-testid={`lt-name-${idx}`} />
                      </div>
                      <div className="space-y-1 w-24">
                        <Label className="text-xs">Annual Quota</Label>
                        <Input type="number" value={lt.annual_quota} onChange={(e) => updateLeaveType(idx, 'annual_quota', parseInt(e.target.value) || 0)} className="h-9 text-sm" data-testid={`lt-quota-${idx}`} />
                      </div>
                      <div className="space-y-1 w-28">
                        <Label className="text-xs">Admin Only</Label>
                        <button
                          type="button"
                          className={`h-9 w-full rounded-md border text-xs font-medium transition-colors ${lt.admin_controlled ? 'bg-violet-100 border-violet-300 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' : 'bg-background border-input text-muted-foreground hover:bg-muted'}`}
                          onClick={() => updateLeaveType(idx, 'admin_controlled', !lt.admin_controlled)}
                          data-testid={`lt-admin-${idx}`}
                        >
                          {lt.admin_controlled ? 'Admin Ctrl' : 'Standard'}
                        </button>
                      </div>
                      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive shrink-0" onClick={() => removeLeaveType(idx)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Optional Holidays */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Optional Holidays List</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addOptionalHoliday} className="gap-1" data-testid="add-oh-btn">
                    <Plus className="w-3.5 h-3.5" /> Add Holiday
                  </Button>
                </div>
                <div className="space-y-2">
                  {policyForm.optional_holidays?.map((oh, idx) => (
                    <div key={idx} className="flex items-end gap-2">
                      <div className="space-y-1 flex-1">
                        <Label className="text-xs">Name</Label>
                        <Input value={oh.name} onChange={(e) => updateOptionalHoliday(idx, 'name', e.target.value)} placeholder="Diwali" className="h-9 text-sm" data-testid={`oh-name-${idx}`} />
                      </div>
                      <div className="space-y-1 w-40">
                        <Label className="text-xs">Date</Label>
                        <Input type="date" value={oh.date} onChange={(e) => updateOptionalHoliday(idx, 'date', e.target.value)} className="h-9 text-sm" data-testid={`oh-date-${idx}`} />
                      </div>
                      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive shrink-0" onClick={() => removeOptionalHoliday(idx)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {(!policyForm.optional_holidays || policyForm.optional_holidays.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">No optional holidays added yet</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button type="button" variant="outline" onClick={() => setIsPolicyOpen(false)}>Cancel</Button>
                <Button onClick={savePolicy} disabled={savingPolicy} className="gap-2" data-testid="save-policy-btn">
                  {savingPolicy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Policy
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeavesPage;
